import { LoadingStateModel } from '@/models/loadingState';
import CaseService from '@/services/caseService';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useStudyStore } from './study';
import { PumpPipelineFilter, type CaseModel } from '@/models/case';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { useFormStore } from './forms';
import type { PaginatorData } from '@/components/common/interfaces/PaginatorData';
import { FormModel } from '@/models';


export const usePipelineStore = defineStore('pipeline', () => {

  const tableColumns = ref<TableColumnModel[]>([
    {
      id: 1,
      header: 'Case Label',
      key: 'label',
      type: TableColumnType.TEXT_WTIH_ICON,
      icon: {
        icon: 'folder',
        style: 'fas',
        color: 'rgb(133, 198, 255)'
      },
      sortable: false,
      frozen: true
    }
  ]);

  const formStore = useFormStore();
  const studyStore = useStudyStore();
  const caseList = ref<CaseModel[]>([]);
  const totalCasesCount = ref<number>(0);
  const currentPage = ref(1);
  const pageLimit = ref(10);
  const searchText = ref('');

  const isPumpStudy = computed(() => studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP');
  const pumpFilter = ref<PumpPipelineFilter | undefined>(PumpPipelineFilter.ACTIVE);

  const buildTableFormColumns = async () => {
    if (formStore.formList.length == 0) {
      await formStore.getStudyForms();
    }
    
    if (tableColumns.value.length === 1) {
      formStore.formList.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      formStore.formList.forEach((form, index) => {
        tableColumns.value.push({
          id: index + 2,
          header: form.title,
          key: form.identifier,
          type: TableColumnType.PIPELINE,
          sortable: false,
          identifier: form.identifier
        });
      });
    }
  };

  const casesLoader = ref(new LoadingStateModel({isLoading: false}));
  const getPipelineCases = async () => {
    casesLoader.value.startLoading();
    await buildTableFormColumns();
    try {
      let filter = pumpFilter.value;
      if (!isPumpStudy.value) {
        filter = undefined;
      }
      const caseResponse =
        await CaseService.getCases(
          studyStore.selectedStudyId!,
          currentPage.value,
          pageLimit.value,
          searchText.value,
          'pipeline',
          'createdAt',
          undefined,
          filter
        );
      caseList.value = caseResponse.cases;
      totalCasesCount.value = caseResponse.casesCount;
    } catch (error) {
      casesLoader.value.setError(error);
    } finally {
      casesLoader.value.stopLoadingAndFinish();
      await mapFormEnforcements(formStore.formList);
    }
  };

  const handleCaseSearch = (searchQuery: string) => {
    currentPage.value = 1;
    searchText.value = searchQuery;
    getPipelineCases();
  };

  const mapFormEnforcements = async (forms: FormModel[]) => {
    for (const caseItem of caseList.value) {
      for (let i = 0; i < forms.length; i++) {
        const form = forms[i];
        if (caseItem.enforcements && caseItem.enforcements[form.identifier]?.enforceOrder) {
          if (!caseItem.entries || (caseItem.entries && caseItem.entries[form.identifier].length == 0)) {
            forms.forEach((formItem, index) => {
              if (index > i && formItem.identifier !== form.identifier && caseItem.enforcements) {
                caseItem.enforcements[formItem.identifier].enforceOrder = true;
              }
            });
          }
          break;
        }
      }
    }
  };

  const onPageChange = (pageData: PaginatorData) => {
    currentPage.value = pageData.page + 1;
    pageLimit.value = pageData.rows;
    getPipelineCases();
  };

  const resetStore = () => {
    tableColumns.value.splice(1, tableColumns.value.length - 1);
    currentPage.value = 1;
  };

  return {
    getPipelineCases,
    onPageChange,
    handleCaseSearch,
    resetStore,
    tableColumns,
    casesLoader,
    caseList,
    totalCasesCount,
    currentPage,
    pageLimit,
    pumpFilter,
    isPumpStudy
  };
});
